import React, { useEffect, useRef, useState } from "react";
import PageLayout from "../common/PageLayout";
import { BsFiles } from "react-icons/bs";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SubheaderTitle from "../common/SubheaderTitle";
import { PiUploadSimple } from "react-icons/pi";
import { AiFillCheckCircle } from "react-icons/ai";
import { HiOutlineTrash } from "react-icons/hi2";
import { TbClick } from "react-icons/tb";
import { AppEntryRepository } from "../repositories/AppEntryRepository";
import { Enumerations } from "../../config/Enumerations";
import { useTheme } from "@emotion/react";
import { PermanentTrafficChangeFormValidator } from "../validators/AppEntryValidator";
import ApplicantDataForm from "../common/ApplicantDataForm";
import { GoFile } from "react-icons/go";
import { UserRepository } from "../repositories/UserRepository";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import { useNavigate } from "react-router-dom";
import { ProjectDataForm } from "./ProjectDataForm";

const files = [
  {
    label: "Прикачи сообраќаен проект PDF",
    accept: ".pdf",
    key: "trafficPlanPdf",
  },
  {
    label: "Прикачи сообраќаен проект DWG",
    accept: ".dwg",
    key: "trafficPlanDwg",
  },
  {
    label: "Прикачи ревизија",
    accept: ".dwg, .pdf",
    key: "projectRevision",
  },
  {
    label: "Прикачи урбанистички план PDF",
    accept: ".pdf",
    key: "urbanPlanPdf",
  },
  {
    label: "Прикачи урбанистички план DWG",
    accept: ".dwg",
    key: "urbanPlanDwg",
  },
];

const optionalFiles = [
  {
    label: "Прикачи полномошно овластување",
    accept: ".pdf, .јpg, .png, image/jpeg",
    key: "powerOfAttorney",
  },
  {
    label: "Прикачи доказ за уплата",
    accept: ".pdf, .png, image/jpeg",
    key: "paymentReceipt",
  },
];

export default function PermanentTrafficChangeForm() {
  const theme = useTheme();
  const auth = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    name: "",
    headquarters: "",
    city: "",
    phone: "",
    email: "",
  });
  const fileInputRefs = useRef({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [formDataErrors, setFormDataErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    populateFormData();
  }, []);

  const populateFormData = () => {
    setSaving(true);
    UserRepository.getCurrentUser()
      .then((res) => {
        const user = res.data;
        let populateDataObject = {
          name: (user?.firstName + " " || "") + (user?.lastName || ""),
          phone: user?.phoneNumber || "",
          email: user?.email || "",
        };
        setFormData(populateDataObject);
        setSaving(false);
      })
      .catch((err) => {
        console.log(err);
        setSaving(false);
      });
  };

  const handleFileUpload = (e, key) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFiles((prev) => ({
        ...prev,
        [key]: file,
      }));
    }
  };

  const handleFileRemove = (key) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [key]: null,
    }));

    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key].value = "";
    }
  };

  const createAppEntry = async () => {
    setSaving(true);
    const formPayload = new FormData();
    formPayload.append(
      "appEntryType",
      Enumerations.AppEntryType.PERMANENT_TRAFFIC_CHANGE,
    );
    formPayload.append("applicantName", formData.name || "");
    formPayload.append("applicantAddress", formData.headquarters || "");
    formPayload.append("applicantPlace", formData.city || "");
    formPayload.append("applicantPhone", formData.phone || "");
    formPayload.append("applicantEmail", formData.email || "");
    formPayload.append("projectTitle", formData.projectTitle || "");
    formPayload.append(
      "projectTechnicalNumber",
      formData.projectTechnicalNumber || "",
    );
    formPayload.append(
      "locationDescription",
      formData.locationDescription || "",
    );
    formPayload.append("projectBureauName", formData.projectBureauName || "");
    Object.entries(uploadedFiles).forEach(([key, file]) => {
      if (file) {
        formPayload.append(key, file);
      }
    });
    const plainData = formDataToObject(formPayload);
    const isValid = await validateForm(plainData);
    if (!isValid) {
      setSaving(false);
      return;
    }

    AppEntryRepository.create(formPayload)
      .then((res) => {
        setSaving(false);
        setTrackingNumber(res.data);
        dispatch(notifyShowSuccessMessage("Барањето е успешно поднесено."));
      })
      .catch((err) => {
        setSaving(false);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
        console.log(err);
      });
  };

  const validateForm = async (data) => {
    try {
      await PermanentTrafficChangeFormValidator.validate(data, {
        abortEarly: false,
      });
      setFormDataErrors({});
      return true;
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach((validationError) => {
        validationErrors[validationError.path] = validationError.message;
      });
      console.log(validationErrors);
      setFormDataErrors(validationErrors);
      return false;
    }
  };

  const formDataToObject = (formData) => {
    const obj = {};
    for (let [key, value] of formData.entries()) {
      obj[key] = value;
    }

    obj.uploadedFiles = {
      trafficPlanPdf: formData.get("trafficPlanPdf"),
      trafficPlanDwg: formData.get("trafficPlanDwg"),
      urbanPlanPdf: formData.get("urbanPlanPdf"),
      urbanPlanDwg: formData.get("urbanPlanDwg"),
      projectRevision: formData.get("projectRevision"),
    };

    return obj;
  };

  const handleRowClick = (key) => {
    const input = fileInputRefs.current[key];
    if (input) {
      input.click();
    } else {
      console.warn("Input ref not found for key", key);
    }
  };

  return (
    <PageLayout
      title="Барање за трајна измена на режим за сообраќај"
      Icon={BsFiles}
    >
      <Grid container spacing={2}>
        {!trackingNumber ? (
          <Grid item xs={12} md={12}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(255, 255, 255, 0.6)",
                  zIndex: 10,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress
                  size={60}
                  sx={{ color: theme.palette.primary.main }}
                />
              </Box>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SubheaderTitle title="Податоци за барателот" />{" "}
                <Box style={{ padding: "0 0 10px 0" }}>
                  <Alert variant="outlined" severity="info">
                    Барањето за времена измена на режим за сообраќај се
                    поднесува согласно член 392 од Закон за безбедност на
                    сообраќајот на патиштата (Службен весник на РМ број 54/2007,
                    86/2008 и 98/2008). Како прилог на барањето се прикачува
                    комплетен сообраќаен план во четири примероци изработен
                    според пропишаните стандарди и нормативи за проектирање
                    согласно член 33 и член 34 од Закон за градење (Службен
                    весник на РМ број 51/2004 и 82/2008), како и заверен
                    урабнистички проект или детален план, со кој се верифицира
                    локациската поставеност на објектот што се приклучува на
                    патот."
                  </Alert>
                </Box>
                <ApplicantDataForm
                  formData={formData}
                  setFormData={setFormData}
                  formDataErrors={formDataErrors}
                />
                <SubheaderTitle title="Податоци за проектот" />
                <ProjectDataForm
                  formData={formData}
                  setFormData={setFormData}
                  formDataErrors={formDataErrors}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SubheaderTitle title="Потребни документи за прикачување" />
                <Box>
                  <Table
                    size="small"
                    sx={{
                      borderCollapse: "separate",
                      borderSpacing: "0 8px",
                    }}
                  >
                    <TableBody>
                      {files.map((item, index) => {
                        const errorKey = `uploadedFiles.${item.key}`;
                        const fileError = formDataErrors?.[errorKey];
                        return (
                          <React.Fragment key={index}>
                            <TableRow
                              onClick={(e) => {
                                handleRowClick(item.key);
                                e.stopPropagation();
                              }}
                              style={{
                                cursor: "pointer",
                                margin: "3px",
                              }}
                            >
                              <TableCell
                                sx={{
                                  textAlign: "left",
                                  verticalAlign: "middle",
                                  border: "1px solid #ccc",
                                  borderRight: "none",
                                  borderBottom: "1px solid #ccc",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {item.key === "projectRevision" ? (
                                    <GoFile
                                      size={22}
                                      style={{
                                        marginRight: "8px",
                                        marginLeft: "2px",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={
                                        item.accept === ".pdf"
                                          ? `${process.env.PUBLIC_URL}/assets/img/pdf-file.svg`
                                          : item.accept.includes(".dwg")
                                            ? `${process.env.PUBLIC_URL}/assets/img/dwg-file.svg`
                                            : `${process.env.PUBLIC_URL}/assets/img/img-icon-file.png`
                                      }
                                      width="25px"
                                      height="25px"
                                      style={{ marginRight: "8px" }}
                                    />
                                  )}
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <Typography>{item.label}</Typography>
                                    {uploadedFiles[item.key] && (
                                      <Typography
                                        variant="caption"
                                        style={{ fontSize: "12px" }}
                                      >
                                        {uploadedFiles[item.key].name}
                                      </Typography>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell
                                sx={{
                                  textAlign: "right",
                                  verticalAlign: "middle",
                                  border: "1px solid #ccc",
                                  borderLeft: "none",
                                  borderBottom: "1px solid #ccc",
                                }}
                              >
                                <div
                                  style={{
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                  onClick={(e) => {
                                    fileInputRefs.current[item.key]?.click();
                                    e.stopPropagation();
                                  }}
                                >
                                  {uploadedFiles[item.key] ? (
                                    <AiFillCheckCircle
                                      color="#4caf50"
                                      size={20}
                                    />
                                  ) : (
                                    <PiUploadSimple
                                      color={theme.palette.secondary.main}
                                      size={20}
                                    />
                                  )}

                                  {uploadedFiles[item.key] && (
                                    <IconButton
                                      onClick={(e) => {
                                        handleFileRemove(item.key);
                                        e.stopPropagation();
                                      }}
                                      size="small"
                                      style={{
                                        padding: 4,
                                        backgroundColor: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: "auto",
                                      }}
                                    >
                                      <HiOutlineTrash
                                        color="#f44336"
                                        size={20}
                                      />
                                    </IconButton>
                                  )}
                                </div>
                                <input
                                  ref={(el) =>
                                    (fileInputRefs.current[item.key] = el)
                                  }
                                  type="file"
                                  accept={item.accept}
                                  onChange={(e) =>
                                    handleFileUpload(e, item.key)
                                  }
                                  hidden
                                />
                              </TableCell>
                            </TableRow>
                            {fileError && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ paddingTop: 0 }}>
                                  <Typography
                                    color="error"
                                    variant="body2"
                                    sx={{ paddingLeft: 2 }}
                                  >
                                    {fileError}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
                <div style={{ marginTop: "20px" }} />
                <SubheaderTitle title="Опционални документи за прикачување" />
                <Alert variant="outlined" severity="info">
                  Полномошно овластување се прикачува само во случај кога
                  корисникот поднесува барање за измена на сообраќаен режим за
                  друго лице или за правно лице. Уплатница е потребно да се
                  прикачи кога корисникот административната такса ја има платено
                  во банка, во спротивно корисникот ќе биде пренасочен кон
                  страна за онлајн плаќање.
                </Alert>
                <Table
                  size="small"
                  sx={{
                    borderCollapse: "separate",
                    borderSpacing: "0 8px",
                  }}
                >
                  <TableBody>
                    {optionalFiles.map((item, index) => (
                      <TableRow
                        key={index}
                        onClick={() => handleRowClick(item.key)}
                        style={{ cursor: "pointer", margin: "3px" }}
                      >
                        <TableCell
                          sx={{
                            textAlign: "left",
                            verticalAlign: "middle",
                            border: "1px solid #ccc",
                            borderRight: "none",
                            borderBottom: "1px solid #ccc",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <img
                              src={
                                item.accept === ".pdf"
                                  ? `${process.env.PUBLIC_URL}/assets/img/pdf-file.svg`
                                  : item.accept.includes(".dwg")
                                    ? `${process.env.PUBLIC_URL}/assets/img/dwg-file.svg`
                                    : `${process.env.PUBLIC_URL}/assets/img/img-icon-file.png`
                              }
                              width="25px"
                              height="25px"
                              style={{ marginRight: "8px" }}
                            />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <Typography>{item.label}</Typography>
                              {uploadedFiles[item.key] && (
                                <Typography
                                  variant="caption"
                                  style={{ fontSize: "12px" }}
                                >
                                  {uploadedFiles[item.key].name}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "right",
                            verticalAlign: "middle",
                            border: "1px solid #ccc",
                            borderLeft: "none",
                            borderBottom: "1px solid #ccc",
                          }}
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                            onClick={(e) => {
                              fileInputRefs.current[item.key]?.click();
                              e.stopPropagation();
                            }}
                          >
                            {uploadedFiles[item.key] ? (
                              <AiFillCheckCircle color="#4caf50" size={20} />
                            ) : (
                              <PiUploadSimple
                                color={theme.palette.secondary.main}
                                size={20}
                              />
                            )}

                            {uploadedFiles[item.key] && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFileRemove(item.key);
                                }}
                                size="small"
                                style={{
                                  padding: 4,
                                  backgroundColor: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "auto",
                                }}
                              >
                                <HiOutlineTrash color="#f44336" size={20} />
                              </IconButton>
                            )}
                          </div>
                          <input
                            ref={(el) => (fileInputRefs.current[item.key] = el)}
                            type="file"
                            accept={item.accept}
                            onChange={(e) => handleFileUpload(e, item.key)}
                            hidden
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "15px 0",
                }}
              >
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "200px",
                  }}
                  onClick={() => createAppEntry()}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: "Inter-Regular",
                        fontSize: "16px",
                        color: "#fafafa",
                      }}
                    >
                      Поднеси
                    </Typography>
                    <TbClick fontSize={21} color="#fafafa" />
                  </div>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                textAlign: "center",
                marginTop: "50px",
                padding: "20px",
                backgroundColor: "#fff",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.primary.main,
                }}
              >
                Вашето барање е успешно поднесено!
              </Typography>
              <Typography variant="h6" sx={{ color: "#000" }}>
                Идентификатор со кој можете да го следите статусот на барањето:{" "}
                <strong style={{ color: theme.palette.primary.main }}>
                  {trackingNumber}
                </strong>
              </Typography>
              <Button
                size="large"
                variant="contained"
                color="primary"
                style={{
                  width: "200px",
                  marginTop: "20px",
                }}
                onClick={() => {
                  setTrackingNumber(null);
                  setFormData({
                    name: "",
                    headquarters: "",
                    city: "",
                    phone: "",
                    email: "",
                  });
                  setUploadedFiles({});
                  navigate("/entries/submitted");
                }}
              >
                <Typography
                  style={{
                    fontFamily: "Inter-Regular",
                    fontSize: "16px",
                    color: "#fff",
                  }}
                >
                  Кон мои барања
                </Typography>
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
      {saving && (
        <Backdrop open={true} sx={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </PageLayout>
  );
}
